import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import FormInput from '@/shared/components/forms/FormInput';
import FormSelect from '@/shared/components/forms/FormSelect';

interface IAddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (user: { name: string; email: string; role: string }) => void | Promise<void>;
  organizationName: string;
}

interface IAddUserForm {
  name: string;
  email: string;
  role: string;
}

const ROLE_OPTIONS = [
  { value: 'org_admin', label: 'Org Admin' },
  { value: 'qa_manager', label: 'QA Manager' },
  { value: 'qa', label: 'QA Staff' },
];

const AddUserModal = ({ isOpen, onClose, onAdd, organizationName }: IAddUserModalProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm<IAddUserForm>({
    defaultValues: {
      name: '',
      email: '',
      role: 'QA Staff',
    },
  });

  const roleValue = watch('role');

  useEffect(() => {
    if (isOpen) {
      reset({
        name: '',
        email: '',
        role: 'QA Staff',
      });
    }
  }, [isOpen, reset]);

  if (!isOpen) {
    return null;
  }

  const onValid = (data: IAddUserForm) => {
    onAdd(data);
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <div
      className='fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5'
      onClick={handleClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          handleClose();
        }
      }}
      role='button'
      tabIndex={0}
      aria-label='Close modal'
    >
      <div
        className='bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl'
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role='dialog'
        tabIndex={-1}
      >
        <div className='flex justify-between items-center p-6 border-b border-gray-200 gap-4'>
          <h2 className='text-xl font-semibold text-gray-900 m-0'>
            Add User to <span className='text-theme-600'>{organizationName}</span>
          </h2>
          <button
            type='button'
            className='bg-transparent border-none cursor-pointer text-gray-500 p-1 flex items-center justify-center rounded shrink-0 hover:text-gray-900 hover:bg-gray-100'
            onClick={handleClose}
            aria-label='Close'
          >
            <svg
              width='20'
              height='20'
              viewBox='0 0 20 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M15 5L5 15M5 5L15 15'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit(onValid)} className='p-6'>
          <FormInput
            name='name'
            label='Name'
            labelClassName='text-sm font-medium text-gray-700 mb-2'
            placeholder='Enter name'
            register={register}
            errors={errors}
            validation={{ required: 'Name is required' }}
            containerClassName='mb-4'
            className='!rounded-md !py-2 !text-sm border-gray-300'
          />

          <FormInput
            name='email'
            type='email'
            label='Email Address'
            labelClassName='text-sm font-medium text-gray-700 mb-2'
            placeholder='user@example.com'
            register={register}
            errors={errors}
            validation={{
              required: 'Email address is required',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Please enter a valid email address',
              },
            }}
            containerClassName='mb-4'
            className='!rounded-md !py-2 !text-sm border-gray-300'
          />

          <FormSelect
            name='role'
            label='Role'
            placeholder='Select role'
            options={ROLE_OPTIONS}
            register={register}
            errors={errors}
            value={roleValue}
            validation={{ required: 'Role is required' }}
            containerClassName='mb-6'
            className='!rounded-md !py-2 !text-sm !border-gray-300'
          />

          <div className='flex justify-end gap-3 pt-4 border-t border-gray-200'>
            <button
              type='button'
              className='px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 cursor-pointer'
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type='submit'
              className='px-4 py-2 bg-theme-500 text-white rounded-md text-sm font-semibold hover:bg-theme-600 cursor-pointer border-none'
            >
              Add User
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;
